import { z } from 'zod';
import { EventAndEntityNameMap } from '../actions/v1/events/email-events';

const optionalTemplateIdSchema = z.preprocess(
  value =>
    typeof value === 'string' && value.trim() === '' ? undefined : value,
  z.string().trim().min(1).optional()
);

export const createEmailTemplateSchema = z
  .object({
    templateId: optionalTemplateIdSchema,
    // TODO: add enum for events with emails
    event: z.string(),
    active: z.boolean(),
    organizationId: z.string(),
    workspaceId: z.string().optional(),
    submissionContextId: z.string().array().optional().default([]).optional(),
    customizable: z.boolean(),
    category: z.string().optional(),
    override: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    scope: z.enum(['journal', 'global']),
    phases: z.array(z.enum(['sub', 'resub', 'rev'])).optional(),
    state: z.string().optional(),
    reminderRound: z.array(z.number()).optional(),
    to: z.string().array().min(1),
    cc: z.string().array().optional().default([]),
    bcc: z.string().array().optional().default([]),
    body: z.string(),
    subject: z.string(),
    tag: z.string().optional(),
    default: z.boolean().optional()
  })
  .superRefine((data, ctx) => {
    if (data.scope === 'journal') {
      if (!data.templateId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['templateId'],
          message: 'templateId is required for journal templates.'
        });
      }

      if (!data?.submissionContextId?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['submissionContextId'],
          message: 'submissionContextId is required for journal templates.'
        });
      }
    }

    if (data.scope === 'global') {
      if (data.templateId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['templateId'],
          message: 'templateId is not allowed for global templates.'
        });
      }

      if (data?.submissionContextId?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['submissionContextId'],
          message: 'submissionContextId is only valid for journal templates.'
        });
      }
    }
  });

export const patchEmailTemplateSchema = z
  .object({
    templateId: optionalTemplateIdSchema,
    customizable: z.boolean().optional(),
    organizationId: z
      .any()
      .transform(() => undefined)
      .optional(),

    state: z.string().optional(),
    reminderRound: z.array(z.number()).optional(),
    event: z.string().optional(),
    scope: z.enum(['journal', 'global']).optional(),
    override: z.string().optional(),
    description: z.string().optional(),
    submissionContextId: z.array(z.string()).optional(),
    to: z.array(z.string()).min(1).optional(),
    cc: z.array(z.string()).optional(),
    bcc: z.array(z.string()).optional(),
    active: z.boolean().optional(),
    category: z.string().optional(),
    name: z.string().optional(),
    phases: z.array(z.enum(['sub', 'resub', 'rev'])).optional(),
    body: z.string().optional(),
    subject: z.string().optional(),
    tag: z.string().optional(),
    default: z.boolean().optional()
  })
  .superRefine((data, ctx) => {
    if (data.scope === 'journal') {
      if (!data.templateId?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['templateId'],
          message: 'templateId is required when scope is journal.'
        });
      }

      if (data.submissionContextId && data.submissionContextId.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['submissionContextId'],
          message: 'submissionContextId cannot be empty.'
        });
      }
    }

    if (data.scope === 'global') {
      if (data.templateId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['templateId'],
          message: 'templateId is not allowed when scope is global.'
        });
      }

      if (data.submissionContextId && data.submissionContextId.length !== 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['submissionContextId'],
          message: 'submissionContextId should be empty.'
        });
      }
    }
  });
const recipientsSchema = z.object({
  to: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  bcc: z.array(z.string()).optional()
});

const attachmentSchema = z.object({
  name: z.string().min(1),
  url: z.string().url()
});

const userRefSchema = z.object({
  name: z.string(),
  id: z.string()
});

export const validateDefaultSchema = z.object({
  event: z.string(),
  default: z.boolean(),
  tag: z.string().optional()
});

export const validateEmailTemplateSchema = z.object({
  _id: z.string(),
  templateId: z.string().optional(),
  event: z.string(),
  organizationId: z.string(),
  workspaceId: z.string().optional(),
  submissionContextId: z.array(z.string()).optional(),
  customizable: z.boolean(),
  category: z.string().optional(),
  name: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  scope: z.enum(['journal', 'global']),
  phases: z.array(z.enum(['sub', 'resub', 'rev'])),
  override: z.string().optional(),
  active: z.boolean().optional(),
  state: z.string().optional(),
  reminderRound: z.array(z.number()).optional(),
  tag: z.string().optional(),

  recipients: recipientsSchema,

  editedBy: userRefSchema.optional(),
  createdBy: userRefSchema.optional(),

  subject: z.string().optional(),
  body: z.string().optional(),
  attachments: z.array(attachmentSchema).optional(),
  default: z.boolean().optional()
});

export const orgParamSchema = z.object({
  organizationId: z.string().min(1)
});

export const idParamSchema = z.object({
  organizationId: z.string().min(1),
  id: z.string().min(1)
});

export const eventIdParamSchema = z.object({
  eventId: z.string().min(1)
});

export const eventParamSchema = z.object({
  organizationId: z.string().min(1),
  eventName: z.string().min(1),
  state: z.string().optional()
});

export const orgAndCommandParamSchema = z.object({
  organizationId: z.string().min(1),
  commandName: z.string().min(1)
});

export const actionOrSubTypeCommandQuerySchema = z
  .object({
    subtype: z.string().optional(),
    action: z.string().optional(),
    tag: z.string().optional(),
    submissionContextId: z.string().optional()
  })
  .optional();

export const listEmailTemplateQuerySchema = z.object({
  event: z.array(z.string()).optional(),
  submissionContextId: z.array(z.string()).optional(),
  scope: z.array(z.string()).optional(),
  category: z.array(z.string()).optional(),
  search: z.string().optional(),
  active: z.array(z.string()).optional(),
  phases: z.array(z.string()).optional(),
  skip: z.coerce.number().min(0).optional().default(0),
  limit: z.coerce.number().min(1).max(100).optional().default(20)
});

export const listTemplateCategoriesQuerySchema = z.object({
  search: z.string().optional(),
  skip: z.coerce.number().min(0).optional().default(0),
  limit: z.coerce.number().min(1).max(100).optional().default(20)
});

export const templateCategoriesBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional()
});

export const eventIdsQuerySchema = z.object({
  eventIds: z.array(z.string().min(1)).min(1),
  user: z
    .object({
      email: z.string(),
      role: z.string()
    })
    .optional()
});

export type TransitionWorkflowType =
  (typeof EventAndEntityNameMap)[keyof typeof EventAndEntityNameMap];
const workflowTypes = Object.keys(
  EventAndEntityNameMap
) as readonly TransitionWorkflowType[];

export const reminderInDaysSchema = z.object({
  reminderDays: z.array(z.number()).min(1, {
    message: 'At least 1 reminder day is required'
  }),
  workflowState: z.string().min(1, {
    message: 'Task or State is required in order to update reminder days'
  }),
  workflowType: z.enum(
    workflowTypes as [TransitionWorkflowType, ...TransitionWorkflowType[]]
  )
});
